import { useEffect, useCallback } from 'react'
import { useAuth } from '@/contexts/AuthContext'

interface A11yPrefs {
    high_contrast: boolean
    reduce_motion: boolean
    large_text: boolean
    dyslexia_font: boolean
}

const A11Y_CLASSES: (keyof A11yPrefs)[] = [
    'high_contrast',
    'reduce_motion',
    'large_text',
    'dyslexia_font',
]

function prefKeyToClass(key: keyof A11yPrefs): string {
    return key.replace(/_/g, '-')
}

export function useAccessibility() {
    const { user, updatePreferences } = useAuth()
    const prefs = user?.preferences?.global

    const a11y: A11yPrefs = {
        high_contrast: prefs?.high_contrast ?? false,
        reduce_motion: prefs?.reduce_motion ?? false,
        large_text: prefs?.large_text ?? false,
        dyslexia_font: prefs?.dyslexia_font ?? false,
    }

    useEffect(() => {
        const html = document.documentElement
        for (const key of A11Y_CLASSES) {
            html.classList.toggle(prefKeyToClass(key), a11y[key])
        }

        // Sync localStorage for anti-FOUC
        localStorage.setItem('argalis-a11y', JSON.stringify(a11y))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [a11y.high_contrast, a11y.reduce_motion, a11y.large_text, a11y.dyslexia_font])

    const toggle = useCallback(
        (key: keyof A11yPrefs, value: boolean) => {
            updatePreferences({ global: { [key]: value } })
        },
        [updatePreferences],
    )

    return { a11y, toggle }
}
